import { useEffect, useState } from "react";
import { ThemeTypeEnum } from "../../../assets/utils/validation/types";
import Panel from "../../ui/panel";
import { LayoutProps } from "../layout";
import styled from "styled-components";
import { COLORS } from "../../../assets/utils/enums/colors";
import AppHeader from "./header";
import { Button } from "../../ui/button";
import { Toaster } from "react-hot-toast";
import Loader from "../../ui/loader";
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css'; // Import CSS once
import breakpoints from "../../../assets/styles/breakpoint";
import {
  BoldTextBody,
  CaptionText,
  LabelText,
  LittleHeadline,
  LittleSubHeadline,
  MediumText,
  NotificationText,
  SubTitle,
} from "../../typography";
import { BodyDiv } from "../../components-style";
import { TableBlock } from "../../ui/table";
import { InputStyle } from "../../ui/inputs";
import ModalWindow from "../../ui/modal-window";
import ScrollToTop from "../scroll-top";

const AppLayout: React.FC<React.PropsWithChildren<LayoutProps>> = ({
  className,
  children,
  loaderState,
  blockType,
  theme = ThemeTypeEnum.DEFAULT,
  modalWindow,
  isMinHeight
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (width <= 1024) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };
  }, [width]);

  function setPanelState() {
    setIsCollapsed(!isCollapsed);
  }

  return (
    <>
      <ScrollToTop />
      <div className="w-100 min-vh-100 d-flex ">
        <Panel
          isCollapsed={isCollapsed}
          setIsCollapsed={setPanelState}
          theme={ThemeTypeEnum.LIGHT}
        />
        <BodyBlock $isCollapsed={isCollapsed} className="flex-grow-1">
          <AppHeader />
          <div data-type={blockType} data-is-min-height={isMinHeight}>{children}</div>
          <Tooltip id="info-tooltip" className="custom-tooltip-styles" />
        </BodyBlock>
      </div>
      {loaderState && <Loader />}
      <ModalWindow
        isOpen={modalWindow?.isOpen ?? false}
        onConfirm={modalWindow?.onConfirm ?? (() => { })}
        onCancel={modalWindow?.onCancel ?? (() => { })}
        action={modalWindow?.action!}
      />
      <Toaster
        toastOptions={{
          style: {
            padding: 0,
            margin: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
            width: "100%",
            maxWidth: "none",
          },
        }}
      />
    </>
  );
};

export default AppLayout;

export const MainBody = styled.div`
  display: flex;
  align-items: start;
  gap: 16px;
`;

const BodyBlock = styled.div<{ $isCollapsed: boolean }>`
  background-color: ${COLORS.gray};
  margin-left: ${({ $isCollapsed }) => ($isCollapsed ? "4rem" : "15rem")};
  &::selection,
  & > *::selection {
    color: ${COLORS.white};
    background: ${COLORS.blue};
  }

  ${CaptionText} {
    color: ${COLORS.secondary1};
  }
  width: calc(100% - 15rem);
  [data-class="statistics-block"] {
    /* margin-top: 0;
          padding: 36px 40px !important; */
    display: flex;
    gap: 16px;
  }

  div {
    &[data-type="main"],
    &[data-type="settings"] {
      padding: 32px;
    }
    &[data-type="projects"],
    &[data-type="details-project"] {
      padding: 22px 32px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    &[data-is-min-height="true"] {
      min-height: calc(100vh - 82px);

    }
    &[data-type="item-form"] {
      padding: 22px 32px;
      width: 100%;
      .center-btn {
        margin: 32px auto;
      }

      [data-class="add-block"],
      [data-class="add-block-other"],
      [data-class="add-block-members"] {
        /* padding: 16px 0; */
        margin-top: 16px;
        padding: 32px !important;
        ${CaptionText} {
          color: ${COLORS.secondary1} !important;
        }
        min-height: auto;
      }

      [data-class="add-block"] {
        width: 60%;
        ${CaptionText} {
          padding-bottom: 8px;
          text-align: center;
        }
      }
      [data-class="add-block-other"] {
        width: 40%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;

        ${CaptionText} {
          text-align: center;
        }

        ${InputStyle} {
          padding-top: 1.5px;
        }
      }
    }
    &[data-type="settings"] {
      ${MainBody} {
        margin-top: 16px;
        [data-class="settings-bar"] {
          min-width: 270px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 100%;

 ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background-color: ${COLORS.gray};
    border-radius: 100px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${COLORS["light-gray"]};
    border: 1px solid ${COLORS["bg-text-color"]};
    border-radius: 100px;
  }
          ${Button} {
            margin: 0;
            margin-left: 12px;
            img {
              width: 30px;
              height: 30px;
            }
          }

          p {
            white-space: nowrap;
          }

          ${breakpoints.xl} {
            min-width: 250px;
          }
        }
      }
    }
    &[data-type="details-project"] {
      ${BodyDiv} {
        padding: 20px;
      }
      ${TableBlock} {
        padding: 20px;
      }
      [data-class="detail-main-block"] {
        width: 50%;
        height: auto;
        min-height: auto;
        display: flex;
        flex-direction: column;
        gap: 20px;
        img {
          width: 24px;
          height: 24px;
        }
        ${LittleSubHeadline}, ${NotificationText} {
          color: ${COLORS["bg-text-color"]};
        }
      }
      ${LittleHeadline}, ${BoldTextBody} {
        color: ${COLORS.secondary1};
      }
      [data-class="detail-add-block"] {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        gap: 20px;
        ${BodyDiv} {
          min-height: auto;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
      }
      [data-class="add-block-members"],
      [data-class="add-comment"] {
        /* padding: 16px 0; */
        /* margin-top: 16px; */
        padding: 20px !important;
        ${CaptionText}, ${SubTitle} {
          color: ${COLORS.secondary1} !important;
        }
        min-height: auto;
      }
      [data-class="add-comment"] {
        ${MediumText} {
          color: ${COLORS.secondary1};
          white-space: break-spaces;
          span {
            color: ${COLORS.blue};
          }
        }
      }
      [data-class="add-block-members"] {
        ${InputStyle} {
          padding: 6px 0;
          span {
            border: 2px solid ${COLORS.gray};
          }
          input {
            pointer-events: none; /* блокує всі кліки */
            user-select: none;
          }
        }
        ${Button} {
          padding: 8px 16px;
        }
        ${LabelText}, ${NotificationText} {
          display: none;
        }
      }
    }
  }

  @media (max-width: 1024px) {
    margin-left: 4em;
  }

  .custom-tooltip-styles {
  background-color: ${COLORS["light-gray"]};
  color: ${COLORS["bg-text-color"]};
  border: 1px solid ${COLORS["light-bg-color"]};
  border-radius: 12px;
  opacity: 1 !important;
  --rt-opacity: 1 !important;
  ${NotificationText.componentStyle.rules.join("")}
  padding: 12px;
  width: 300px !important;
  z-index: 1003;

  .react-tooltip-arrow {
    display: none !important;
  }
 }

.help-icon {
      width: 22px;
      height: 22px;
      cursor: pointer;
      transition: all 200ms cubic-bezier(0.25, 0.1, 0.25, 1) !important;

    &:hover {
      transform: scale(1.1);
    }
    }

  .accent-text {
    color: ${COLORS.blue} !important;
  }

  .warning-text {
    color: ${COLORS.warning} !important;
  }
`;
